/* eslint-disable */
/**
 * useVendorPopup — manages the vendor popup window for the National Partners flow.
 *
 * - Opens with fixed 1280×800 dimensions, centered via screen.width/screen.height
 * - Features: menubar=no,toolbar=no,status=no,scrollbars=yes,resizable=yes
 * - Reuses the existing window if already open (same windowName = "RetailerWindow")
 * - Returns false from openPopup if the popup was blocked by the browser
 * - Polls window.closed to detect popup closure
 * - Cleans up polling interval on unmount
 *
 * Requirements: 8.1, 8.2, 8.4, 8.5, 8.6
 */
import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseVendorPopupOptions {
  /** The window name used to identify/reuse the popup. Default: "RetailerWindow" */
  windowName?: string;
}

export interface UseVendorPopupReturn {
  /** Opens the popup to the given URL. Returns false if the popup was blocked. */
  openPopup: (url: string) => boolean;
  /** Whether the popup window is currently open. */
  isOpen: boolean;
  /** Programmatically close the popup window. */
  closePopup: () => void;
}

const POPUP_WIDTH = 1280;
const POPUP_HEIGHT = 800;
const POLL_INTERVAL_MS = 500;
const DEFAULT_WINDOW_NAME = 'RetailerWindow';

/**
 * Builds the window.open features string with the popup centered on screen.
 */
function buildPopupFeatures(): string {
  const left = Math.round((screen.width - POPUP_WIDTH) / 2);
  const top = Math.round((screen.height - POPUP_HEIGHT) / 2);
  return [
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'status=no',
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');
}

/**
 * Hook for managing a vendor popup window in the National Partners flow.
 *
 * Requirements: 8.1, 8.2, 8.4, 8.5, 8.6
 */
export function useVendorPopup(options: UseVendorPopupOptions = {}): UseVendorPopupReturn {
  const windowName = options.windowName ?? DEFAULT_WINDOW_NAME;
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  /**
   * Starts the polling loop that watches for the popup to be closed.
   */
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) return; // Already polling

    pollIntervalRef.current = setInterval(() => {
      if (popupRef.current === null || popupRef.current.closed) {
        // Popup was closed (or was never opened)
        popupRef.current = null;
        setIsOpen(false);
        if (pollIntervalRef.current !== null) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, POLL_INTERVAL_MS);
  }, []);

  /**
   * Stops the polling loop without closing the popup.
   */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /**
   * Opens the vendor popup to the given URL.
   * Reuses the existing window if it is still open (same windowName).
   * Returns false if the popup was blocked by the browser.
   *
   * Requirements: 8.1, 8.2, 8.4
   */
  const openPopup = useCallback(
    (url: string): boolean => {
      const features = buildPopupFeatures();

      // window.open with a named window reuses the existing window if open,
      // or opens a new one if it is closed or hasn't been opened yet.
      const popup = window.open(url, windowName, features);

      if (!popup) {
        // Popup was blocked by the browser (Requirement 8.5 / 3.5)
        return false;
      }

      // Bring the popup to the foreground in case it was reused
      popup.focus();

      popupRef.current = popup;
      setIsOpen(true);
      startPolling();
      return true;
    },
    [windowName, startPolling]
  );

  /**
   * Programmatically closes the vendor popup window.
   */
  const closePopup = useCallback(() => {
    stopPolling();
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    setIsOpen(false);
  }, [stopPolling]);

  // Clean up the polling interval when the hook unmounts to prevent memory leaks.
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return { openPopup, isOpen, closePopup };
}
